import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Brand } from '../components/Brand';
import { LegalDocumentText } from '../components/LegalDocumentText';
import { acceptFeatureRelease, downloadSignedAgreement, getRelease } from '../lib/api';
import { demoMode, supabase } from '../lib/supabase';
import type { ReleasePortal } from '../types';

function normalizedFullName(value: string): string {
  return value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();
}

export function Release() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returningToPortal = searchParams.get('continue') === 'portal';
  const [release, setRelease] = useState<ReleasePortal | null>(null);
  const [checked, setChecked] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [businessRepresented, setBusinessRepresented] = useState('');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [acceptError, setAcceptError] = useState('');
  const [copyError, setCopyError] = useState('');
  const fullNameMatchesRecord = Boolean(release && normalizedFullName(typedName) === normalizedFullName(release.participantName));

  useEffect(() => {
    async function load() {
      try {
        if (!demoMode && supabase) {
          const { data } = await supabase.auth.getSession();
          if (!data.session) {
            navigate(`/login?next=${encodeURIComponent(`/release/${slug}${returningToPortal ? '?continue=portal' : ''}`)}`, { replace: true });
            return;
          }
        }
        const data = await getRelease(slug);
        // The private-portal route skips the standalone signed-release receipt.
        if (returningToPortal && data.release.acceptedAt) {
          navigate(`/portal/${encodeURIComponent(slug)}`, { replace: true });
          return;
        }
        setRelease(data);
        // Each new agreement version requires a freshly typed legal signature.
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unable to load participant release.');
      } finally { setLoading(false); }
    }
    load();
  }, [slug, navigate, returningToPortal]);

  async function signOut() {
    await supabase?.auth.signOut();
    navigate(`/login?next=${encodeURIComponent(`/release/${slug}${returningToPortal ? '?continue=portal' : ''}`)}`, { replace: true });
  }

  async function accept() {
    if (!release || !checked || !fullNameMatchesRecord) return;
    setWorking(true); setAcceptError('');
    try {
      const result = await acceptFeatureRelease(slug, typedName.trim(), businessRepresented.trim());
      if (returningToPortal) {
        // After Step 1 go straight to Step 2, not an intermediate thank-you page.
        navigate(`/portal/${encodeURIComponent(slug)}`, { replace: true });
      } else {
        // A pre-filming release can still be signed independently.
        setRelease({ ...release, release: { ...release.release, acceptedAt: result.acceptedAt, typedName: result.typedName } });
      }
    } catch (e) { setAcceptError(e instanceof Error ? e.message : 'Unable to record release.'); }
    finally { setWorking(false); }
  }

  if (loading) return <div className="center-screen"><Brand /><p>Loading your ROAM release…</p></div>;
  if (error || !release) return <div className="center-screen"><Brand /><h1>We couldn’t open this release.</h1><p>{error}</p></div>;

  return <main className="gate-page">
    <nav className="topbar"><Brand small /><div className="portal-account"><span className="private-label">Your story with ROAM</span>{!demoMode && <button className="text-link text-link--button" onClick={signOut}>Sign out</button>}</div></nav>
    <section className="gate-shell">
      <div className="eyebrow">{release.release.acceptedAt ? 'WELCOME TO ROAM' : 'BEFORE WE BEGIN'}</div>
      <h1>{release.release.acceptedAt ? 'Thank you for being part of ROAM.' : 'Let’s make it official.'}</h1>
      {release.release.acceptedAt ? <>
        <p className="lede">Thank you, {release.participantName}. We appreciate you trusting us with your story. Your agreement is signed and safely saved, and we’re excited to tell your story.</p>
        <div className="agreement-card"><div className="agreement-card__header"><div><strong>{release.release.name}</strong><span>Signed and saved · {new Date(release.release.acceptedAt).toLocaleString()}</span></div><span className="pill">Signed</span></div></div>
        <p className="microcopy">Your private ROAM page has its own access terms. You’ll review those next before opening your feature.</p>
        {!demoMode && <button type="button" className="button button--light button--wide" onClick={async () => { try { await downloadSignedAgreement(slug, 'feature_release'); } catch (e) { setCopyError(e instanceof Error ? e.message : 'Could not retrieve your signed copy.'); } }}>Download a copy for your records</button>}
        {copyError && <p className="error">{copyError}</p>}
        <p className="microcopy">Your copy includes your signed name, date and time, and the exact agreement you accepted.</p>
        <Link className="button button--light button--wide" to={`/portal/${encodeURIComponent(slug)}`}>Continue to your private ROAM experience ↗</Link>
      </> : <>
        <p className="lede">{release.participantName}, we’re excited to share your story. Please take a moment to read the release below, then add your name to continue. Your ROAM feature is free, and anything extra is always optional.</p>
        <div className="agreement-card"><div className="agreement-card__header"><div><strong>{release.release.name}</strong><span>Version {release.release.version}</span></div><span className="pill">Participant agreement · Step 1</span></div><div className="agreement-copy"><LegalDocumentText body={release.release.body} /></div></div>
        <label className="legal-name-field">Full name<input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Your full name" autoComplete="name" aria-describedby="roam-participant-name-hint" /></label>
        <p id="roam-participant-name-hint" className="microcopy">We have your name as <strong>{release.participantName}</strong>.</p>
        {typedName.trim().length >= 2 && !fullNameMatchesRecord && <p className="error" role="alert">That looks a little different from the name we have. Double-check it, or let us know if we should update it.</p>}
        <label className="legal-name-field">Business or company (optional)<input value={businessRepresented} onChange={(e) => setBusinessRepresented(e.target.value)} placeholder="Business name (if any)" /></label>
        <label className="consent-row"><input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} /><span>I’ve read and agree to the ROAM Participant Release.</span></label>
        {acceptError && <p className="error">{acceptError}</p>}
        <button className="button button--light button--wide" disabled={!checked || !fullNameMatchesRecord || working} onClick={accept}>{working ? 'Saving your signature…' : 'Agree & continue'}</button>
        <p className="microcopy">We’ll keep a copy of your agreement with your name and the date for your records.</p>
      </>}
    </section>
  </main>;
}
